import { useCallback, useEffect, useState } from "react";
import {
  BriefcaseBusiness,
  ChevronRight,
  LoaderCircle,
  MapPinned,
  Plus,
  UserRoundCheck,
} from "lucide-react";

import { getWorkforce } from "./api";
import { TeamPanel } from "./TeamPanel";
import type { View } from "./AdminChrome";
import type { Employee, Region, Team, WorkforceOverview } from "./types";
import { WorkforceEditor } from "./WorkforceEditor";

type Section = Extract<View, "teams" | "employees" | "regions">;
type Selected = { kind: Section; record?: Employee | Region | Team };

const pageCopy = {
  teams: {
    eyebrow: "Flexible crews",
    title: "Build teams around the work ahead.",
    copy: "Organize team membership, regional coverage, and standard working hours without fixing today’s structure forever.",
    singular: "team",
  },
  employees: {
    eyebrow: "People & capabilities",
    title: "Keep every cleaner’s working profile clear.",
    copy: "Store contact details, capabilities, status, and a default region while team assignments remain separately dated.",
    singular: "employee",
  },
  regions: {
    eyebrow: "Service coverage",
    title: "Define where TLC can thoughtfully serve.",
    copy: "Maintain named regions and cities as live configuration, ready for Juliana’s final service-area decisions.",
    singular: "region",
  },
} as const;

export function WorkforcePage({ section, canWrite }: { section: Section; canWrite: boolean }) {
  const [overview, setOverview] = useState<WorkforceOverview>({
    regions: [],
    employees: [],
    teams: [],
  });
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [selected, setSelected] = useState<Selected>();
  const [teamId, setTeamId] = useState<string>();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setOverview(await getWorkforce());
      setFailed(false);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => void load(), [load]);

  const items = overview[section];
  const copy = pageCopy[section];
  return (
    <div className="page-content workforce-page">
      <header className="page-intro client-intro">
        <div>
          <span className="eyebrow lime">{copy.eyebrow}</span>
          <h1>{copy.title}</h1>
          <p>{copy.copy}</p>
        </div>
        {canWrite ? (
          <button
            className="primary-button compact-action"
            onClick={() => setSelected({ kind: section })}
          >
            <Plus /> Add {copy.singular}
          </button>
        ) : null}
      </header>

      <section className="workforce-summary" aria-label={`${section} summary`}>
        <SummaryMetric label="Teams" value={overview.teams.length} icon={BriefcaseBusiness} />
        <SummaryMetric
          label="Active employees"
          value={overview.employees.filter((item) => item.status === "ACTIVE").length}
          icon={UserRoundCheck}
        />
        <SummaryMetric
          label="Active regions"
          value={overview.regions.filter((item) => item.active).length}
          icon={MapPinned}
        />
      </section>

      {failed ? (
        <div className="load-error" role="alert">
          We couldn’t refresh the organization.
          <button onClick={() => void load()}>Try again</button>
        </div>
      ) : null}

      <section className="content-card workforce-card">
        <div className="section-heading">
          <div>
            <span className="eyebrow">Organization</span>
            <h2>{capitalize(section)}</h2>
          </div>
          <span className="catalog-count">{items.length}</span>
        </div>
        {loading && items.length === 0 ? (
          <div className="table-state">
            <LoaderCircle className="spin" /> Loading {section}…
          </div>
        ) : null}
        {!loading && items.length === 0 ? (
          <div className="workforce-empty">
            <strong>No {section} configured yet.</strong>
            <span>Add the first record only when the real operating information is known.</span>
          </div>
        ) : null}
        <div className="workforce-list">
          {section === "teams"
            ? overview.teams.map((item) => (
                <TeamCard key={item.id} team={item} onOpen={() => setTeamId(item.id)} />
              ))
            : null}
          {section === "employees"
            ? overview.employees.map((item) => (
                <EmployeeCard
                  key={item.id}
                  employee={item}
                  onOpen={() => setSelected({ kind: "employees", record: item })}
                />
              ))
            : null}
          {section === "regions"
            ? overview.regions.map((item) => (
                <RegionCard
                  key={item.id}
                  region={item}
                  onOpen={() => setSelected({ kind: "regions", record: item })}
                />
              ))
            : null}
        </div>
      </section>

      {selected ? (
        <WorkforceEditor
          selection={selected}
          regions={overview.regions}
          canWrite={canWrite}
          onClose={() => setSelected(undefined)}
          onSaved={async () => {
            setSelected(undefined);
            await load();
          }}
        />
      ) : null}
      {teamId ? (
        <TeamPanel
          teamId={teamId}
          employees={overview.employees}
          regions={overview.regions}
          canWrite={canWrite}
          onClose={() => setTeamId(undefined)}
          onChanged={load}
        />
      ) : null}
    </div>
  );
}

function SummaryMetric({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: number;
  icon: typeof BriefcaseBusiness;
}) {
  return (
    <article>
      <span>
        <Icon /> {label}
      </span>
      <strong>{value}</strong>
    </article>
  );
}

function TeamCard({ team, onOpen }: { team: Team; onOpen: () => void }) {
  return (
    <button className="workforce-row" onClick={onOpen}>
      <span className="workforce-icon">
        <BriefcaseBusiness />
      </span>
      <span className="workforce-copy">
        <strong>{team.name}</strong>
        <small>
          {team.defaultRegionName ?? "No default region"} · {team.memberCount} active{" "}
          {team.memberCount === 1 ? "member" : "members"}
        </small>
      </span>
      <Availability active={team.active} />
      <ChevronRight />
    </button>
  );
}

function EmployeeCard({ employee, onOpen }: { employee: Employee; onOpen: () => void }) {
  return (
    <button className="workforce-row" onClick={onOpen}>
      <span className="workforce-icon">
        <UserRoundCheck />
      </span>
      <span className="workforce-copy">
        <strong>{employee.name}</strong>
        <small>
          {employee.defaultRegionName ?? "No default region"} ·{" "}
          {employee.capabilities.length
            ? employee.capabilities.join(" · ")
            : "No capabilities recorded"}
        </small>
      </span>
      <Availability active={employee.status === "ACTIVE"} />
      <ChevronRight />
    </button>
  );
}

function RegionCard({ region, onOpen }: { region: Region; onOpen: () => void }) {
  return (
    <button className="workforce-row" onClick={onOpen}>
      <span className="workforce-icon">
        <MapPinned />
      </span>
      <span className="workforce-copy">
        <strong>{region.name}</strong>
        <small>
          {region.cities.length
            ? region.cities.map((city) => city.city).join(" · ")
            : "No cities configured"}
        </small>
      </span>
      <Availability active={region.active} />
      <ChevronRight />
    </button>
  );
}

function Availability({ active }: { active: boolean }) {
  return (
    <span className={`availability ${active ? "active" : ""}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function capitalize(value: string) {
  return value.charAt(0).toUpperCase() + value.slice(1);
}
